import {useTranslation} from 'react-i18next';
import {Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import React, {Fragment, useEffect} from 'react';
import {makeStyles} from '@mui/styles';
import uuidv4 from 'uuid/v4';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {hasOneOfPermission, Permissions, user} from '../../../store/reducers/user';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import * as _set from 'lodash/set';
import * as _filter from 'lodash/filter';
import * as _find from 'lodash/find';
import Formatter from '../../../util/formatter';
import SepsisPathwayForm from './sepsisPathwayForm';
import {additionalData, updatePatientAdditionalData} from '../../../store/reducers/additionalData';
import config from '../../../app/config';
import AddIcon from '@mui/icons-material/Add';
import API from '../../../app/api';
import {nextWorkflowStep, workflow} from '../../../store/reducers/workflow';
import {addNoteForPatient} from '../../../store/reducers/notes';
import Notes from '../../../util/notes';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleSwitch from '../../../components/ui/switch/agyleSwitch';
import FormLabel from '@mui/material/FormLabel';
import clsx from 'clsx';
import {isNull} from 'lodash';
import {setBlockContext} from '../../../store/reducers/globals';
import {getTableScrollbar} from '../../../util/scrollbar';
import {SepsisPathwayStatus} from './sepsisUtil';
import {WORKFLOW_OPTIONS} from '../../../util/workflowUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    modal: {
        position: 'absolute',
        background: '#535252',
        width: '80%',
        zIndex: 99,
        minHeight: '72%',
        overflow: 'auto',
        padding: '20px',
        paddingBottom: '10px',
        margin: '0 10% 0% 10%',
        maxHeight: 500,
        marginTop: 'calc(50vh - 36vh)',
    },
    backgroundModal: {
        position: 'absolute',
        background: 'black',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: '0.7',
        zIndex: 99,
    },
    closeIcon: {
        position: 'absolute',
        right: '3px',
        top: '4px',
    },
    rightAlign: {
        textAlign: 'right',
    },
    addBtn: {
        position: 'relative',
        top: '15px',
    },
    checked: {
        background: '#69BB69',
        textAlign: 'center',
        padding: '10px 20px',
        maxWidth: '280px',
    },
    toggleContainer: {
        paddingTop: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 15,
        flexBasis: 'content',
        minWidth: '325px',
    },
    checkedBody: {
        height: '62px',
        display: 'table-cell',
        verticalAlign: 'middle',
    },
    required: {
        width: '100%',
        height: '100%',
    },
    checkBtn: {
        width: '66%',
        height: '35px',
        background: '#C33131',
        display: 'inline-block',
        textAlign: 'center',
        lineHeight: '35px',
        cursor: 'pointer',
    },
    naBtn: {
        width: '34%',
        height: '35px',
        background: '#FFA500',
        display: 'inline-block',
        textAlign: 'center',
        lineHeight: '35px',
        cursor: 'pointer',
    },
    timer: {
        height: '35px',
        lineHeight: '35px',
        textAlign: 'center',
    },
    notRequired: {
        background: '#FFA500',
        textAlign: 'center',
        padding: '10px 20px',
        maxWidth: '280px',
    },
    actionFilters: {
        paddingBottom: 16,
        marginTop: 6,
        paddingRight: 1,
    },
    table: {
        minWidth: 650,
        borderCollapse: 'separate',
        borderSpacing: '0px 6px',
    },
    tableHeader: {
        backgroundColor: theme.palette.background.header,
        borderTopLeftRadius: '15px',
        borderBottomLeftRadius: '15px',
        borderBottomRightRadius: '15px',
        borderTopRightRadius: '15px',
        '& td': {
            border: 0,
        },
        '& th': {
            border: 0,
        },
        // borderRadius: '15px'
    },
    tableRow: {
        boxShadow: `0px ${theme.palette.mode === 'light' ? 2 : 0}px ${theme.palette.mode === 'light' ? 1 : 0}px 0px rgba(0, 0, 0, 0.25)`,
        backgroundColor: theme.palette.background.paperDarker,
        borderRadius: theme.palette.mode === 'light' ? 10 : 0,
        marginTop: '10px',
        '& td': {
            border: 0,
        },
        '& th': {
            border: 0,
        },
    },
    borderRadiusLeft: {
        borderTopLeftRadius: '10px',
        borderBottomLeftRadius: '10px',
        // overflow: 'hidden'
    },
    borderRadiusRight: {
        borderTopRightRadius: '10px',
        borderBottomRightRadius: '10px',
        // overflow: 'hidden'
    },
    tableResults: {
        padding: 10,
    },
    formControlDT: {
        backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#313233',
        borderRadius: 6,
        borderColor: theme.palette.admissionForm.borderColor,
    },
    inlineBlock: {
        display: 'inline-flex',
        marginTop: 2,
    },
    dateHeight: {
        height: 18,
    },
    footer: {
        width: '100%',
        marginTop: '40px',
    },
    fullWidth: {
        width: '100%',
    },
    cancel: {
        background: 'indianred',
        color: 'white',
    },
    pathwayRow: {
        height: '90px',
        marginBottom: '10px',
        display: 'flex',
    },
    pathwayTitle: {
        display: 'inline-flex',
        height: '100%',
        width: 'calc(100% - 280px)',
    },
    pathwayActions: {
        display: 'inline-flex',
        width: '280px',
        maxWidth: '280px',
        marginTop: '5px',
        marginBottom: '5px',
    },
    headerTitle: {
        textAlign: 'center',
        padding: '10px 25px',
        fontSize: '22px',
        backgroundColor: '#757575',
        marginBottom: '10px',
    },
    headerSubtitle: {
        fontSize: '16px',
        padding: '5px 25px',
        marginTop: '-10px',
        backgroundColor: '#a1a1a1',
    },
    pathNumber: {
        height: '100%',
        verticalAlign: 'middle',
        backgroundColor: '#6495ed',
        width: '45px',
        textAlign: 'center',
        display: 'inline-block',
        lineHeight: '90px',
        fontSize: '22px',
    },
    pathDescription: {
        paddingLeft: '15px',
        paddingRight: '15px',
        display: 'inline-block',
    },
    subject: {
        fontSize: '16px',
        fontWeight: '500',
        marginTop: '7px',
    },
    reasonModal: {
        width: '400px',
        background: '#535252',
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '10px 20px',
        marginTop: 'calc(50vh - 100px)',
    },
    reasonTexField: {
        width: '100%',
    },
    greenIcon: {
        color: theme.palette.mode === 'dark' ? '#4EAC6F' : 'inherit',
        backgroundColor: theme.palette.mode === 'light' ? '#4EAC6F' : 'inherit',
        borderRadius: 4,
        width: 60,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: 3,
        lineHeight: '10px',
    },
    redIcon: {
        color: theme.palette.mode === 'dark' ? '#E25B61' : 'inherit',
        backgroundColor: theme.palette.mode === 'light' ? '#E25B61' : 'inherit',
        borderRadius: 4,
        width: 60,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: 3,
        lineHeight: '10px',
    },
    naResult: {
        cursor: 'help',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignItems: 'flex-end',
    },
    divColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableCell: {
        padding: 10,
    },
    label: {
        color: theme.palette.mode === 'light' ? '#626466' : '#ACAFB3',
        fontWeight: 'bold',
        fontSize: 14,
        marginLeft: 2,
    },
}));

const BASE_FILTER = {
    withCompleted: true,
    currentAttendance: true,
    dateFrom: null,
    dateTo: null,
};

function SepsisPathwayTableView(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User data
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Selectors
    const patientAddData = useSelector(additionalData);
    const workflowData = useSelector(workflow);

    // Permissions
    const canEditSepsis = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_SEPSIS]));

    // Sepsis states
    const baseSepsisObj = {
        id: uuidv4() + '',
        dateCreate: null,
        userCreate: tester,
        userCreateLogin: userObject.userName,
        userCreateId: userObject.__id,
        relatedPatientInWardId: null,
        neutropenic: false,
        informST3: null,
        informST3CheckedBy: null,
        informST3CheckedById: null,
        informST3CheckedByDate: null,
        informST3TimeoutDate: null,
        informST3NAReason: null,
        informST3UncheckReason: null,
        informST3UncheckUser: null,
        informST3UncheckUserId: null,
        oxygen: null,
        oxygenCheckedBy: null,
        oxygenCheckedById: null,
        oxygenCheckedByDate: null,
        oxygenTimeoutDate: null,
        oxygenNAReason: null,
        oxygenUncheckReason: null,
        oxygenUncheckUser: null,
        oxygenUncheckUserId: null,
        fluids: null,
        fluidsCheckedBy: null,
        fluidsCheckedById: null,
        fluidsCheckedByDate: null,
        fluidsTimeoutDate: null,
        fluidsNAReason: null,
        fluidsUncheckReason: null,
        fluidsUncheckUser: null,
        fluidsUncheckUserId: null,
        bloods: null,
        bloodsCheckedBy: null,
        bloodsCheckedById: null,
        bloodsCheckedByDate: null,
        bloodsTimeoutDate: null,
        bloodsNAReason: null,
        bloodsUncheckReason: null,
        bloodsUncheckUser: null,
        bloodsUncheckUserId: null,
        mbc: null,
        mbcCheckedBy: null,
        mbcCheckedById: null,
        mbcCheckedByDate: null,
        mbcTimeoutDate: null,
        mbcNAReason: null,
        mbcUncheckReason: null,
        mbcUncheckUser: null,
        mbcUncheckUserId: null,
        antibiotics: null,
        antibioticsCheckedBy: null,
        antibioticsCheckedById: null,
        antibioticsCheckedByDate: null,
        antibioticsTimeoutDate: null,
        antibioticsNAReason: null,
        antibioticsUncheckReason: null,
        antibioticsUncheckUser: null,
        antibioticsUncheckUserId: null,
        balance: null,
        balanceCheckedBy: null,
        balanceCheckedById: null,
        balanceCheckedByDate: null,
        balanceTimeoutDate: null,
        balanceNAReason: null,
        balanceUncheckReason: null,
        balanceUncheckUser: null,
        balanceUncheckUserId: null,
        ivio: null,
        ivioCheckedBy: null,
        ivioCheckedById: null,
        ivioCheckedByDate: null,
        ivioTimeoutDate: null,
        ivioNAReason: null,
        ivioUncheckReason: null,
        ivioUncheckUser: null,
        ivioUncheckUserId: null,
        hrorrr: null,
        hrorrrCheckedBy: null,
        hrorrrCheckedById: null,
        hrorrrCheckedByDate: null,
        hrorrrTimeoutDate: null,
        hrorrrNAReason: null,
        hrorrrUncheckReason: null,
        hrorrrUncheckUser: null,
        hrorrrUncheckUserId: null,
        venous: null,
        venousCheckedBy: null,
        venousCheckedById: null,
        venousCheckedByDate: null,
        venousTimeoutDate: null,
        venousNAReason: null,
        venousUncheckReason: null,
        venousUncheckUser: null,
        venousUncheckUserId: null,
        fluidoverload: null,
        fluidoverloadCheckedBy: null,
        fluidoverloadCheckedById: null,
        fluidoverloadCheckedByDate: null,
        fluidoverloadTimeoutDate: null,
        fluidoverloadNAReason: null,
        fluidoverloadUncheckReason: null,
        fluidoverloadUncheckUser: null,
        fluidoverloadUncheckUserId: null,
        statusObj: null,
        dateEnd: null,
        userEnd: null,
        userEndId: null,
        notRequiredReason: null,
        paeds: null,
    };
    const [viewSepsisPathway, setViewSepsisPathway] = React.useState(false);
    const [sepsisPathwayObj, setSepsisPathwayObj] = React.useState(baseSepsisObj);
    const [rowsPaeds, setRowsPaeds] = React.useState([]);
    const [rowsAdult, setRowsAdult] = React.useState([]);
    const incompleteSepsisPath = patientAddData?.other?.sepsisPathways ? _find(patientAddData.other.sepsisPathways, ['statusObj', SepsisPathwayStatus.INCOMPLETE]) : null;

    // Others
    const [actionButtons, setActionButtons] = React.useState([]);
    const [arrayFilter, setArrayFilter] = React.useState(BASE_FILTER);
    const defaultTimeoutInMinutes = 60;
    const age = moment().diff(props.patient?.patient?.dob, 'years');

    useEffect(() => {
        const baseSepsisPathways = patientAddData?.other?.sepsisPathways ? patientAddData.other.sepsisPathways : [];
        const adultsSepsisPathways = _filter(baseSepsisPathways, (el) => {
            return !el.paeds;
        });
        const paedsSepsisPathways = _filter(baseSepsisPathways, ['paeds', true]);

        setRowsPaeds(paedsSepsisPathways);
        setRowsAdult(adultsSepsisPathways);
    }, [patientAddData?.other?.sepsisPathways]);

    useEffect(() => {
        if (workflowData.type === WORKFLOW_OPTIONS.SEPSIS_PATHWAY) {
            if (incompleteSepsisPath) {
                setSepsisPathwayObj(incompleteSepsisPath);
                setViewSepsisPathway(true);
            } else {
                addPatientToPathway(workflowData.data.obsData);
            }
        }
    }, [workflowData]);

    useEffect(() => {
        let buttons = [];
        if (!incompleteSepsisPath && canEditSepsis) {
            buttons.push({
                name: 'AddSepsis',
                display: t('ADD SEPSIS PATHWAY'),
                icon: <AddIcon/>,
                color: 'primary',
                variant: 'contained',
                event: addPatientToPathway.bind(this, null),
            });
            if (config?.actionButtonsOnTop) setActionButtons(buttons);
        } else {
            setActionButtons([]);
        }
    }, [incompleteSepsisPath, canEditSepsis]);

    useEffect(() => {
        return () => {
            dispatch(setBlockContext(false));
        };
    }, []);

    const addPatientToPathway = (timeTmp) => {
        let time = timeTmp || moment();

        const startSepsisObj = {...baseSepsisObj};
        startSepsisObj.dateCreate = moment().valueOf();
        startSepsisObj.userCreate = tester;
        startSepsisObj.userCreateLogin = userObject.userName;
        startSepsisObj.userCreateId = userObject.__id;
        startSepsisObj.relatedPatientInWardId = props.patient.__id;
        startSepsisObj.oxygenTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.fluidsTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.bloodsTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.mbcTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.ivioTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.antibioticsTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.balanceTimeoutDate = moment(time).add(defaultTimeoutInMinutes, 'minutes').valueOf();
        startSepsisObj.statusObj = SepsisPathwayStatus.INCOMPLETE;
        setSepsisPathwayObj(startSepsisObj);
        setViewSepsisPathway(true);
        startSepsisObj.paeds = age < 18;
    };

    const changeFilter = (field, value) => {
        let nf = {...arrayFilter};
        if (value) {
            _set(nf, field, value);
        } else {
            _set(nf, field, !arrayFilter[field]);
        }
        setArrayFilter(nf);
    };

    const findIndex = (array, id) => {
        for (let i = 0; i < array.length; i++) {
            if (array[i].id === id) {
                return i;
            }
        }
        return -1;
    };

    const savePathway = (cancel, reason, sepsisObj) => {
        let index;
        if (sepsisObj.paeds) {
            index = findIndex(rowsPaeds, sepsisObj.id);
        } else {
            index = findIndex(rowsAdult, sepsisObj.id);
        }
        let sObj = {...sepsisObj};
        if (cancel) {
            sObj.statusObj = SepsisPathwayStatus.CANCELED;
            sObj.notRequiredReason = reason;
        } else {
            if (sepsisObj.paeds) {
                if (sObj.oxygen != null && sObj.balance != null &&
                    sObj.ivio != null && sObj.bloods != null &&
                    sObj.mbc != null && sObj.fluids != null) {
                    sObj.statusObj = SepsisPathwayStatus.ENDED;
                }
            } else {
                if (sObj.oxygen != null && sObj.fluids != null &&
                    sObj.bloods != null && sObj.mbc != null &&
                    sObj.antibiotics != null && sObj.balance != null) {
                    sObj.statusObj = SepsisPathwayStatus.ENDED;
                }
            }
        }
        if (sObj.statusObj !== SepsisPathwayStatus.INCOMPLETE) {
            sObj.dateEnd = moment().valueOf();
            sObj.userEnd = tester;
            sObj.userEndLogin = userObject.userName;
            sObj.userEndId = userObject.__id;
        }
        //Add or replace
        if (index < 0) {
            dispatch(updatePatientAdditionalData(props.patient.patient.__id, {'sepsisPathways': [...rowsPaeds, ...rowsAdult, sObj]}));
        } else {
            let r, o;
            if (sepsisObj.paeds) {
                r = [...rowsPaeds];
                o = [...rowsAdult];
                r[index] = sObj;
                // setRowsPaeds([...rowsPaeds]);
            } else {
                r = [...rowsAdult];
                o = [...rowsPaeds];
                r[index] = sObj;
                //  setRowsAdult([...rowsAdult]);
            }
            dispatch(updatePatientAdditionalData(props.patient.patient.__id, {'sepsisPathways': [...r, ...o]}));
        }
        updatePatient(sObj);
        saveNotes(sObj);
        //setSepsisPathwayObj({...baseSepsisObj});
        setViewSepsisPathway(false);
        setSepsisPathwayObj(sObj);
        dispatch(nextWorkflowStep());
    };

    const filteredResults = (paeds = false) => {
        if (BASE_FILTER === arrayFilter)
            if (paeds) {
                return Array.from(rowsPaeds).reverse();
            } else {
                return Array.from(rowsAdult).reverse();
            }
        else {
            let r = [];
            if (arrayFilter.withCompleted) {
                if (paeds) {
                    r.push(...rowsPaeds);
                } else {
                    r.push(...rowsAdult);
                }
            } else {
                if (paeds) {
                    rowsPaeds.forEach((row) => {
                        if (row.statusObj === SepsisPathwayStatus.INCOMPLETE) {
                            r.push({...row});
                        }
                    });
                } else {
                    rowsAdult.forEach((row) => {
                        if (row.statusObj === SepsisPathwayStatus.INCOMPLETE) {
                            r.push({...row});
                        }
                    });
                }
            }

            if (arrayFilter.currentAttendance) {
                r = _filter(r, row => {
                    return moment(row.dateCreate).isSameOrAfter(props.patient?.admission?.startDate);
                });
            }

            if (arrayFilter.dateFrom) {
                r = _filter(r, function (row) {
                    return moment(row.dateCreate).isSameOrAfter(arrayFilter.dateFrom, 'day');
                });
            }
            if (arrayFilter.dateTo) {
                r = _filter(r, function (row) {
                    return moment(row.dateCreate).isSameOrBefore(arrayFilter.dateTo, 'day');
                });
            }
            return r.reverse();
        }
    };

    const saveNotes = (sObj) => {
        const note = Notes.getSepsisPathwayNote(sObj);
        dispatch(addNoteForPatient(props.patient.patient.__id, {
            note: note,
            noteType: 'sepsisPathway',
            patientsInWardsRef: [props.patient.__id],
            referenceId: sObj.id,
            episodeId: props?.patient?.admission?.consultantEpisodeId || null,
        }, props.patient.__id));
    };

    const updatePatient = (sObj) => {
        API.updatePatientInWard(props.patient.__id, [{
            'metadata.sepsisPathway': {
                status: sObj.statusObj,
                neutropenic: sObj.neutropenic,
            },
        }]).catch(error => {
            console.error('Observations.API.updatePatientInWard: ', error);
        });
    };

    const renderHeader = () => {
        return (
            <Grid
                container
                alignItems="center"
                className={classes.actionFilters}
                justifyContent="space-between"
                id="sepsis-header"
            >
                <Grid item xs={9} className={classes.divRow} style={{gap: 10}}>
                    <div className={classes.divColumn}>
                        <FormLabel className={classes.label}>
                            {t('Date From')}
                        </FormLabel>

                        <div className={classes.inlineBlock}>
                            <AgyleDatePicker
                                name="arrayFilterDateFrom"
                                value={arrayFilter.dateFrom || null}
                                onChange={(event) => changeFilter('dateFrom', event)}
                                noBorder={false}
                                type="date"
                                textFieldClass={classes.formControlDT}
                                classInput={classes.dateHeight}
                                size="small"
                            />
                        </div>
                    </div>

                    <div className={classes.divColumn}>
                        <FormLabel className={classes.label}>
                            {t('Date To')}
                        </FormLabel>

                        <div className={classes.inlineBlock}>
                            <AgyleDatePicker
                                name="arrayFilterDateFrom"
                                value={arrayFilter.dateTo || null}
                                onChange={(event) => changeFilter('dateTo', event)}
                                noBorder={false}
                                type="date"
                                textFieldClass={classes.formControlDT}
                                classInput={classes.dateHeight}
                                size="small"
                            />
                        </div>
                    </div>

                    <AgyleSwitch
                        onChange={() => changeFilter('withCompleted')}
                        value={arrayFilter.withCompleted}
                        label={t('Include completed pathway')}
                        style={{marginBottom: -4}}
                    />

                    <AgyleSwitch
                        onChange={() => changeFilter('currentAttendance')}
                        value={arrayFilter.currentAttendance}
                        label={t('Show only current activity')}
                        style={{marginBottom: -4}}
                    />
                </Grid>

                <Grid item xs={3} className={classes.divRow}>
                    {actionButtons?.length > 0 && actionButtons.map(btn => (
                        <AgyleButton
                            key={btn?.name}
                            onClick={btn?.event}
                            startIcon={btn?.icon}
                            color={btn?.color}
                            variant={btn?.variant || 'contained'}
                            style={{marginLeft: 'auto'}}
                            fullWidth
                        >
                            {t(btn?.display)}
                        </AgyleButton>
                    ))
                    }
                </Grid>
            </Grid>
        );
    };

    const renderTableHeader = (isAdult) => {
        const adultHeader = () => {
            return (
                <>
                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Fluids')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Bloods')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('MBC')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Antibiotics')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Balance')}
                    </TableCell>
                </>
            );
        };

        const paedsHeader = () => {
            return (
                <>
                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Bp & Urin')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Iv/Io')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Blood')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Meds')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Fluid')}
                    </TableCell>
                </>
            );
        };

        return (
            <TableHead>
                <TableRow className={classes.tableHeader}>
                    <TableCell
                        style={{width: '10%'}}
                        align="left"
                        className={clsx(classes.borderRadiusLeft, classes.tableCell)}
                    >
                        {t('Created')}
                    </TableCell>

                    <TableCell
                        style={{width: '10%'}}
                        align="left"
                        className={classes.tableCell}
                    >
                        {t('User')}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Oxygen')}
                    </TableCell>

                    {isAdult && adultHeader()}
                    {!isAdult && paedsHeader()}

                    <TableCell
                        style={{width: '10%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Status')}
                    </TableCell>

                    <TableCell
                        style={{width: '15%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('Complete/Stop')}
                    </TableCell>

                    <TableCell
                        style={{width: '15%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {t('User')}
                    </TableCell>

                    <TableCell
                        style={{width: '10%'}}
                        align="center"
                        className={clsx(classes.borderRadiusRight, classes.tableCell)}
                    >
                        &nbsp;
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    };

    const renderResult = (data, reason) => {
        return (
            <>
                {data &&
                    <div className={classes.greenIcon}>
                        <CheckIcon/>
                    </div>
                }

                {isNull(data) &&
                    <div className={classes.redIcon}>
                        <ClearIcon/>
                    </div>
                }

                {data === false &&
                    <Tooltip title={t('Reason') + ': ' + reason} arrow>
                        <span className={classes.naResult}>N/A</span>
                    </Tooltip>
                }
            </>
        );
    };

    const renderTableResults = (row, isAdult) => {
        const adultContext = () => {
            return (
                <>
                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.fluids, row.fluidsNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.bloods, row.bloodsNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.mbc, row.mbcNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.antibiotics, row.antibioticsNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.balance, row.balanceNAReason)}
                    </TableCell>
                </>
            );
        };

        const paedContext = () => {
            return (
                <>
                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.balance, row.balanceNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.ivio, row.ivioNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.bloods, row.bloodsNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.mbc, row.mbcNAReason)}
                    </TableCell>

                    <TableCell
                        style={{width: '5%'}}
                        align="center"
                        className={classes.tableCell}
                    >
                        {renderResult(row.fluids, row.fluidsNAReason)}
                    </TableCell>
                </>
            );
        };

        return (
            <TableRow
                key={row.id}
                sx={{'&:last-child td, &:last-child th': {border: 0}, mt: 2}}
                className={classes.tableRow}
            >
                <TableCell
                    style={{width: '10%'}}
                    align="left"
                    component="th"
                    scope="row"
                    className={clsx(classes.borderRadiusLeft, classes.tableCell)}
                >
                    {Formatter.dateFormatted(row.dateCreate, config.defaultDateTimeFormat)}
                </TableCell>

                <TableCell
                    style={{width: '10%'}}
                    align="left"
                    className={classes.tableCell}
                >
                    {row.userCreate}
                </TableCell>

                <TableCell
                    align="center"
                    className={classes.tableCell}
                    style={{width: '5%'}}
                >
                    {renderResult(row.oxygen, row.oxygenNAReason)}
                </TableCell>

                {isAdult && adultContext()}
                {!isAdult && paedContext()}

                <TableCell
                    style={{width: '5%'}}
                    align="center"
                    className={classes.tableCell}
                >
                    {row.statusObj === SepsisPathwayStatus.INCOMPLETE &&
                        <span>{t('incomplete')}</span>
                    }

                    {row.statusObj === SepsisPathwayStatus.CANCELED &&
                        <Tooltip title={t('Reason') + ': ' + row.notRequiredReason} arrow>
                            <span style={{cursor: 'help'}}>{t('not required')}</span>
                        </Tooltip>
                    }

                    {row.statusObj === SepsisPathwayStatus.ENDED &&
                        <span>{t('complete')}</span>
                    }
                </TableCell>

                <TableCell
                    style={{width: '15%'}}
                    align="center"
                    className={classes.tableCell}
                >
                    {Formatter.dateFormatted(row.dateEnd, config.defaultDateTimeFormat)}
                </TableCell>

                <TableCell
                    style={{width: '15%'}}
                    align="center"
                    className={classes.tableCell}
                >
                    {row.userEnd}
                </TableCell>

                <TableCell
                    style={{width: '10%'}}
                    align="right"
                    className={clsx(classes.borderRadiusRight, classes.tableCell)}
                >
                    <AgyleButton
                        onClick={() => {
                            setSepsisPathwayObj(row);
                            setViewSepsisPathway(true);
                        }}
                        size="small"
                        variant="contained"
                        style={{marginLeft: 'auto'}}
                    >
                        {t('View')}
                    </AgyleButton>
                </TableCell>
            </TableRow>
        );
    };

    const getHeight = () => {
        let height = 120;
        let el = document.getElementById('sepsis-header');
        if (el) height += el?.offsetHeight;

        return `calc(100vh - ${height}px)`;
    };

    const renderSepsisTable = () => {
        return (
            <>
                {rowsAdult.length > 0 &&
                    <TableContainer style={{marginBottom: '60px', paddingRight: 3, height: getHeight()}}
                                    className={getTableScrollbar()}>
                        <Table className={classes.table} aria-label="Sepsis pathway">
                            {renderTableHeader(true)}
                            <TableBody>
                                {filteredResults().map((row) => renderTableResults(row, true))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
                {rowsPaeds.length > 0 &&
                    <TableContainer style={{marginBottom: '60px', paddingRight: 3, height: getHeight()}}
                                    className={getTableScrollbar()}>
                        <Table className={classes.table} aria-label="Sepsis pathway">
                            {renderTableHeader(false)}
                            <TableBody>
                                {filteredResults(true).map((row) => renderTableResults(row, false))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
            </>
        );
    };

    return (
        <>
            {!viewSepsisPathway &&
                <div className={classes.tableResults}>
                    {renderHeader()}
                    {renderSepsisTable()}
                </div>
            }

            {(viewSepsisPathway && sepsisPathwayObj) &&
                <SepsisPathwayForm
                    sepsisPathObj={sepsisPathwayObj}
                    disableSave={props.disableSave}
                    closePathwayForm={() => {
                        dispatch(nextWorkflowStep());
                        setViewSepsisPathway(false);
                    }}
                    onPathwayChange={savePathway}
                />
            }
        </>
    );
}

export default SepsisPathwayTableView;